import './utils/partnerToken';
import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  browserPopupRedirectResolver,
  connectAuthEmulator,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { localTestConfig } from './config/localTest';

const local = localTestConfig({
  enabled: process.env.REACT_APP_LOCAL_TEST,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  authHost: process.env.REACT_APP_AUTH_EMULATOR_HOST,
  firestoreHost: process.env.REACT_APP_FIRESTORE_EMULATOR_HOST,
});
if (local && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
  throw new Error('Local-test builds must only be served on localhost.');
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

/** IndexedDB + popup resolver — reliable Google popup return on iOS Safari (custom auth domain). */
let auth;
try {
  auth = process.env.NODE_ENV === 'test'
    ? getAuth(app)
    : initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
} catch {
  auth = getAuth(app);
}

const db = getFirestore(app);
if (local) {
  connectAuthEmulator(auth, `http://${local.authHost}`, { disableWarnings: true });
  const [host, port] = local.firestoreHost.split(':');
  connectFirestoreEmulator(db, host, Number(port));
}

/** Owner/admin browsers never load GA (see utils/ga.js markInternalTraffic). */
function internalTraffic() {
  try {
    if (/^\/(admin|partner)(\/|$)/.test(window.location.pathname || '')) return true;
    return window.localStorage.getItem('cfa_internal_traffic') === '1';
  } catch {
    return false;
  }
}

let analytics;
let analyticsBootstrapped = false;

/**
 * Defer gtag / Firebase Analytics until after window load + idle (or first
 * interaction) so ~160 KB of gtag.js does not compete with LCP. Callers that
 * await analyticsReady still get every event — logEvent runs only after init.
 */
function bootstrapAnalytics() {
  if (analyticsBootstrapped) return;
  analyticsBootstrapped = true;
  if (local || internalTraffic()) {
    analyticsResolve(null);
    return;
  }
  isAnalyticsSupported()
    .then((ok) => {
      if (ok) analytics = getAnalytics(app);
      return analytics || null;
    })
    .catch(() => null)
    .then((instance) => {
      analyticsResolve(instance || null);
    });
}

let analyticsResolve = () => {};
/** Resolves to the Analytics instance, or null when unsupported / local / internal / test. */
const analyticsReady =
  local || internalTraffic() || process.env.NODE_ENV === 'test'
    ? Promise.resolve(null)
    : new Promise((resolve) => {
        analyticsResolve = resolve;
        if (typeof window === 'undefined') {
          resolve(null);
          return;
        }
        const schedule = () => {
          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => bootstrapAnalytics(), { timeout: 4000 });
          } else {
            window.setTimeout(bootstrapAnalytics, 1500);
          }
        };
        const onInteract = () => bootstrapAnalytics();
        for (const evt of ['pointerdown', 'keydown', 'touchstart']) {
          window.addEventListener(evt, onInteract, { once: true, passive: true });
        }
        if (document.readyState === 'complete') schedule();
        else window.addEventListener('load', schedule, { once: true });
      });

export { auth, createUserWithEmailAndPassword, db, analytics, analyticsReady };
